import { MaskOptions, useMask } from '@react-input/mask'
import cn from 'classnames'
import React, {
    InputHTMLAttributes,
    SyntheticEvent,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react'
import { Button, ButtonType } from '@UI/components/controls/Button/Button.tsx'
import { Xcross } from '@UI/components/icons/Xcross.tsx'
import { Gt } from '../../i18n/Gt.tsx'
import useShowHide from '../../utils/useShowHide.ts'
import { Stack } from '../Stack/Stack.tsx'
import { Veil } from '../Veil/Veil.tsx'
import styles from './Input.module.less'
import { useAsyncInputValidation } from './useAsyncInputValidation.tsx'

export type InputValue = InputHTMLAttributes<HTMLInputElement>['value']

export interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value'> {
    onValueChange?: (value: InputHTMLAttributes<HTMLInputElement>['value']) => void
    leftIcon?: React.ReactNode
    rightIcon?: React.ReactNode | 'clear'
    initialValue?: InputValue
    width?: number | string
    black?: boolean
    maskOptions?: MaskOptions
    maxWidth?: number | string
    minWidth?: number | string
    format?: (v: string) => string
    height?: string | number
    validateAsync?: (value?: string) => Promise<string | number> | null | undefined
    grow?: boolean
    maxLength?: number
    wrapperClassName?: string
}

const ClearIcon: React.FC<{ hidden: boolean; onClick: () => void }> = ({ hidden, onClick }) => {
    const className = cn(styles.clearIcon, { [styles.hidden]: hidden })
    return (
        <Button
            stopPropagation
            className={className}
            onClick={onClick}
            type={ButtonType.clickableIcon}
            icon={<Xcross size={16} />}
        />
    )
}

export interface InputHandlers extends HTMLInputElement {
    setValue: (v?: InputValue) => void
    clear: () => void
    value: string
}

export const Input = React.forwardRef<InputHandlers, Props>(
    (
        {
            grow,
            black,
            width, // = '100%',
            rightIcon,
            initialValue = '',
            leftIcon,
            disabled,
            onValueChange,
            className,
            maskOptions,
            maxWidth,
            format,
            height,
            minWidth,
            validateAsync,
            maxLength = 250,
            wrapperClassName,
            onInput,
            ...props
        },
        ref
    ) => {
        const inner = useRef<HTMLInputElement>(null)
        const [value, setValue] = useState<InputValue>(initialValue)
        const [displayValue, setDisplayValue] = useState<InputValue>(initialValue)
        const [dirty, setDirty] = useState<boolean>(false)
        const composition = useShowHide(false)
        useEffect(() => {
            if (initialValue) {
                const value = format ? format(initialValue.toString()) : initialValue
                setValue(value)
                setDisplayValue(value)
                setDirty(true)
            }
        }, [initialValue])

        const clear = useCallback(() => {
            setDisplayValue('')
            setValue('')
            onValueChange && onValueChange('')
            inner.current?.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }))
        }, [Boolean(onValueChange), setDisplayValue, setValue])

        const rightIconEl = useMemo(() => {
            if (!rightIcon) return null
            if (rightIcon !== 'clear') return rightIcon
            return <ClearIcon hidden={value === ''} onClick={clear} />
        }, [rightIcon, value, clear])
        const maskedRef = useMask(maskOptions)

        useImperativeHandle(ref, () => ({
            setValue: (v: InputValue) => {
                if (inner.current) {
                    setValue(v)
                    setDisplayValue(v)
                    onValueChange && onValueChange(v)
                    inner.current.dispatchEvent(new Event('change', { bubbles: true }))
                }
            },
            clear,
            setCustomValidity: (error: string) => {
                inputRef.current?.setCustomValidity(error)
            },
            dispatchEvent: (event: Event): boolean => {
                return inputRef.current?.dispatchEvent(event) || false
            },
            ...inputRef.current,
            //@ts-ignore
            value
        }))

        const onChange = useCallback(
            (e: SyntheticEvent<HTMLInputElement>) => {
                if (e.isTrusted) {
                    if (composition.visible) {
                        //@ts-ignore
                        setDisplayValue(e.target.value)
                        return
                    }
                    //@ts-ignore
                    const value = format ? format(e.target.value) : e.target.value
                    setValue(value)
                    setDisplayValue(value)
                    onValueChange && onValueChange(value)
                    setDirty(true)
                    onInput && onInput(e)
                }
                if (!e.isTrusted && inputRef.current !== e.target)
                    inputRef.current?.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }))
            },
            [onValueChange, format, composition.visible, onInput]
        )

        const inputRef = maskOptions ? maskedRef : inner
        const { validating, validate, networkError, validationMessage } = useAsyncInputValidation(inputRef, validateAsync)

        useEffect(() => {
            inputRef.current?.setCustomValidity(validationMessage || '')
        }, [validationMessage])

        return (
            <Stack grow={grow} direction={'column'}>
                <Veil active={validating}>
                    <div
                        style={{ width, maxWidth, height, minWidth }}
                        className={cn(styles.wrapper, wrapperClassName, { [styles.black]: black })}
                    >
                        {leftIcon && (
                            <div onClick={() => inputRef.current?.focus()} className={styles.leftIcon}>
                                {leftIcon}
                            </div>
                        )}
                        <input
                            {...props}
                            onCompositionStart={composition.open}
                            onCompositionEnd={composition.close}
                            maxLength={maxLength}
                            onBlur={e => {
                                props.onBlur && props.onBlur(e)
                                if (e.target.value) setDirty(true)
                                validate && validate(e.target.value)
                            }}
                            value={displayValue}
                            disabled={disabled || validating}
                            className={cn(styles.root, className, {
                                [styles.withLeftIcon]: Boolean(leftIcon),
                                [styles.withRightIcon]: Boolean(rightIcon),
                                [styles.dirty]: dirty
                            })}
                            style={{ width, maxWidth }}
                            ref={inputRef}
                            minLength={1}
                            onInput={onChange}
                        />
                        {rightIconEl && <div className={styles.rightIcon}>{rightIconEl}</div>}
                    </div>
                </Veil>
                {validationMessage && <span className={styles.validationMessage}>{validationMessage}</span>}
                {networkError && (
                    <span className={styles.validationWarning}>
                        <Gt>Validation failed, server error</Gt>
                    </span>
                )}
            </Stack>
        )
    }
)
